// src/components/Turmas/Turmas.js
import React, { useState, useEffect, useContext, useRef } from "react";
import { FaSearch, FaFilter, FaFileExport, FaChartBar } from "react-icons/fa";
import Modal from "react-modal";
import { AlunoContext } from "../../context/AlunoContext";
import { exportarTurmas } from "../../utils/exportarTurmas";
import FiltrosTurma from "./FiltrosTurma";
import EstatisticaTurma from "./EstatisticaTurma";
import TabelaTurma from "./TabelaTurma";
import styles from "./Turmas.module.css";

const MAX_ALUNOS = 50;

const Turmas = () => {
  const { ativos, setAtivos } = useContext(AlunoContext);
  const [turmas, setTurmas] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState({ ano: "all", classe: "all", turma: "all" });
  const [modalFiltrosOpen, setModalFiltrosOpen] = useState(false);
  const [modalEstatisticaOpen, setModalEstatisticaOpen] = useState(false);
  const [showExportOptions, setShowExportOptions] = useState(false);
  const exportRef = useRef(null);

  // Fecha dropdown exportar
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (exportRef.current && !exportRef.current.contains(e.target)) {
        setShowExportOptions(false);
      }
    };
    if (showExportOptions) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showExportOptions]);

  useEffect(() => {
    if (ativos?.length > 0) organizarTurmas(ativos);
  }, [ativos]);

  const organizarTurmas = (lista) => {
    const ordenados = [...lista].sort((a, b) =>
      (a.nome_completo || "").localeCompare(b.nome_completo || "", "pt", { sensitivity: "base" })
    );
    const turmasTemp = {};
    ordenados.forEach((aluno) => {
      const turmaAtual = aluno.turma || "Sem Turma";
      if (!turmasTemp[turmaAtual]) turmasTemp[turmaAtual] = [];
      const idade = aluno.data_nascimento
        ? Math.floor((new Date() - new Date(aluno.data_nascimento)) / (1000 * 60 * 60 * 24 * 365.25))
        : "-";
      turmasTemp[turmaAtual].push({
        ...aluno,
        codigo: aluno.codigo_aluno || aluno.codigo || "—",
        numero: turmasTemp[turmaAtual].length + 1,
        turma: turmaAtual,
        idade,
        // Normaliza genero (M, F) ou "-" se ausente
        genero: aluno.genero ? String(aluno.genero).trim().toUpperCase() : "-"
      });
    });
    setTurmas(turmasTemp);
  };

  // Filtragem
  const turmasFiltradas = Object.keys(turmas).reduce((acc, key) => {
    let lista = turmas[key];
    if (filters.ano !== "all") lista = lista.filter(a => String(a.ano_criacao || a.ano || "") === String(filters.ano));
    if (filters.classe !== "all") lista = lista.filter(a => a.classe === filters.classe);
    if (filters.turma !== "all") lista = lista.filter(a => a.turma === filters.turma);

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      lista = lista.filter(a =>
        (a.nome_completo || "").toLowerCase().includes(query) ||
        (a.codigo || "").toLowerCase().includes(query)
      );
    }

    if (lista.length) acc[key] = lista.map((a, i) => ({ ...a, numero: i + 1 }));
    return acc;
  }, {});

  // util - conta H/M de um array
  const contarGeneros = (alunos) => {
    let H = 0, M = 0;
    alunos.forEach(a => {
      const g = (a.genero || "").toString().trim().toUpperCase();
      if (g === "M" || g === "MASCULINO" || g === "H") H += 1;
      else if (g === "F" || g === "FEMININO") M += 1;
    });
    return { H, M };
  };

// Título principal (quando exibir detalhe único)
const tituloTurma = () => {
  const keys = Object.keys(turmasFiltradas).filter(k => turmasFiltradas[k].length > 0);
  const todosAlunos = Object.values(turmasFiltradas).flat();

  if (todosAlunos.length === 0)
    return "Aplique ao menos um filtro para visualizar as turmas.";

  // Quando apenas uma turma estiver sendo exibida
  if (keys.length === 1 && keys[0] !== "Sem Turma") {
    const alunos = turmasFiltradas[keys[0]];
    const alunoRef = alunos[0] || {};

    // Ano detectado de forma robusta
    let ano =
      alunoRef.ano_criacao ||
      alunoRef.ano ||
      (alunoRef.codigo_aluno && /^\d{4}/.test(alunoRef.codigo_aluno)
        ? alunoRef.codigo_aluno.substring(0, 4)
        : "—");

    const classe = alunoRef.classe || "—";
    const { H, M } = contarGeneros(alunos);
    const vagasDisponiveis = MAX_ALUNOS - alunos.length;

    return `Turma ${keys[0]}, ${classe} - ${ano} | Total: ${alunos.length} (H:${H} / M:${M}) | Vagas disponíveis: ${vagasDisponiveis}`;
  }

  // Quando houver múltiplas turmas filtradas
  const totalAlunos = todosAlunos.length;
  const { H, M } = contarGeneros(todosAlunos);
  return `Resultado: ${keys.length} turma(s) | Alunos: ${totalAlunos} (H:${H}/M:${M})`;
};

  const handleExport = (formato) => {
    exportarTurmas(turmasFiltradas, formato, "Turmas");
    setShowExportOptions(false);
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2>Gestão de Turmas</h2>
        <div className={styles.actions}>
          <div className={styles.searchBar}>
            <FaSearch />
            <input placeholder="Pesquisar aluno (nome ou código)..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
          </div>

          <button className={styles.filterBtn} onClick={() => setModalFiltrosOpen(true)}><FaFilter /> Filtrar</button>

          <div className={styles.dropdownExport} ref={exportRef}>
            <button className={styles.exportBtn} onClick={() => setShowExportOptions(!showExportOptions)}><FaFileExport /> Exportar</button>
            {showExportOptions && (
              <div className={styles.exportOptions}>
                <button onClick={() => handleExport("pdf")}>PDF</button>
                <button onClick={() => handleExport("excel")}>Excel</button>
                <button onClick={() => handleExport("csv")}>CSV</button>
              </div>
            )}
          </div>

          <button className={styles.filterBtn} onClick={() => setModalEstatisticaOpen(true)}><FaChartBar /> Estatísticas</button>
        </div>
      </div>

      <h3>{tituloTurma()}</h3>

      {/* Se for exibição detalhada de uma única turma, passamos singleView=true para esconder título repetido */}
      <TabelaTurma
        turmasFiltradas={turmasFiltradas}
        setAtivos={setAtivos}
        setTurmas={setTurmas}
        singleView={Object.keys(turmasFiltradas).length === 1}
      />

      {/* Modais */}
      <Modal isOpen={modalFiltrosOpen} onRequestClose={() => setModalFiltrosOpen(false)} className={styles.modal} overlayClassName={styles.modalOverlay}>
        <FiltrosTurma turmas={turmas} filters={filters} setFilters={setFilters} fechar={() => setModalFiltrosOpen(false)} />
      </Modal>

      <Modal isOpen={modalEstatisticaOpen} onRequestClose={() => setModalEstatisticaOpen(false)} className={styles.modal} overlayClassName={styles.modalOverlay}>
        <EstatisticaTurma turmasFiltradas={turmasFiltradas} fechar={() => setModalEstatisticaOpen(false)} />
      </Modal>
    </div>
  );
};

export default Turmas;
